import type { RefObject } from "react";
import { useCallback, useRef, useState } from "react";

import { useResizeObserver } from "app/custom-hooks/useResizeObserver";

/**
 * Measures an element's rendered `offsetHeight` and keeps it in sync.
 *
 * Used to declare a concrete height for an ant-design virtual-table expanded row: the
 * frozen virtual list positions the rows below it from that declared height, so it must
 * be known. Measuring the real content (instead of predicting it from a row count)
 * keeps the height exact for dynamic row counts and variable row heights, leaving no gap.
 */
export function useMeasuredHeight<T extends HTMLElement>(): [RefObject<T | null>, number] {
    const ref = useRef<T>(null);
    const [height, setHeight] = useState(0);

    const measure = useCallback(() => {
        const nextHeight = ref.current?.offsetHeight ?? 0;
        setHeight((prev) => (prev === nextHeight ? prev : nextHeight));
    }, []);

    useResizeObserver({ onResize: measure, observingElement: ref.current });

    return [ref, height];
}
